import { useQuery } from '@tanstack/react-query';
import { apiFetch, StatCard, Skeleton, EmptyState } from './shared';

interface ReferralData {
  stats: {
    links_shared: number;
    signed_up: number;
    first_completes: number;
    points_paid: number;
    fraud_blocks_today: number;
  };
  funnel: Array<{ step: string; count: number; rate?: number }>;
  top_referrers: Array<{
    member_id: string;
    email: string | null;
    display_name: string | null;
    referrals_sent: number;
    converted: number;
    points_earned: number;
  }>;
  recent_activity: Array<{
    date: string;
    referred_member_id: string;
    referred_email: string | null;
    inviter_id: string | null;
    inviter_email: string | null;
    bonus_paid: boolean;
  }>;
  fraud_signals: {
    ip_clusters: Array<{ inviter_id: string; ip: string; count: number }>;
    approaching_daily_cap: Array<{ inviter_id: string; bonuses_today: number }>;
    at_lifetime_cap: Array<{ inviter_id: string; lifetime_bonuses: number }>;
  };
}

export default function ReferralSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ua', 'referral'],
    queryFn: () => apiFetch('/api/ua/admin/referral') as Promise<ReferralData>,
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;

  const { stats, funnel, top_referrers, recent_activity, fraud_signals } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Links Shared" value={stats.links_shared} />
        <StatCard label="Referred Signups" value={stats.signed_up} tone="info" />
        <StatCard label="First Completes" value={stats.first_completes} tone="good" />
        <StatCard label="Points Paid" value={stats.points_paid.toLocaleString()} tone="good" sub="Referrer side" />
        <StatCard
          label="Fraud Blocks"
          value={stats.fraud_blocks_today}
          tone={stats.fraud_blocks_today > 0 ? 'warn' : 'neutral'}
          sub="IP clusters + caps"
        />
      </div>

      <div className="bg-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white mb-4">Funnel</h3>
        <div className="space-y-3">
          {funnel.map((s) => {
            const widthPct = funnel[0]?.count ? Math.max(4, (s.count / funnel[0].count) * 100) : 4;
            return (
              <div key={s.step} className="flex items-center gap-4">
                <div className="w-40 text-sm text-white font-medium">{s.step}</div>
                <div className="flex-1 bg-slate-700 rounded-full h-6 relative overflow-hidden">
                  <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${widthPct}%` }} />
                  <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-medium">
                    {s.count.toLocaleString()} {s.rate != null ? `(${s.rate}%)` : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Top Referrers</h3>
          <p className="text-xs text-slate-500 mt-0.5">Sorted by first-survey completions.</p>
        </div>
        {top_referrers.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No referrers yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Member</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Converted</th>
                <th className="px-3 py-2 text-right">Points Earned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {top_referrers.map((r) => (
                <tr key={r.member_id} className="hover:bg-slate-700/50">
                  <td className="px-3 py-2 text-white">{r.display_name ?? '\u2014'}</td>
                  <td className="px-3 py-2 text-slate-300 text-xs">{r.email ?? '\u2014'}</td>
                  <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{r.referrals_sent}</td>
                  <td className="px-3 py-2 text-right text-green-400 tabular-nums">{r.converted}</td>
                  <td className="px-3 py-2 text-right text-white tabular-nums">{r.points_earned.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Recent Referral Activity</h3>
          <p className="text-xs text-slate-500 mt-0.5">Last 50 referral attribution events.</p>
        </div>
        {recent_activity.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No referral activity yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Referred</th>
                <th className="px-3 py-2">By</th>
                <th className="px-3 py-2">Bonus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {recent_activity.map((r) => (
                <tr key={r.referred_member_id} className="hover:bg-slate-700/50">
                  <td className="px-3 py-2 text-slate-400 text-xs">{new Date(r.date).toLocaleString()}</td>
                  <td className="px-3 py-2 text-slate-300 text-xs">{r.referred_email ?? '\u2014'}</td>
                  <td className="px-3 py-2 text-slate-300 text-xs">{r.inviter_email ?? '\u2014'}</td>
                  <td className="px-3 py-2">
                    {r.bonus_paid
                      ? <span className="text-xs text-green-400">Paid</span>
                      : <span className="text-xs text-slate-500">Pending / not paid</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(fraud_signals.ip_clusters.length > 0
        || fraud_signals.approaching_daily_cap.length > 0
        || fraud_signals.at_lifetime_cap.length > 0) ? (
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-700">
            <h3 className="text-sm font-bold text-white">Fraud signals</h3>
          </div>
          <div className="divide-y divide-slate-700">
            {fraud_signals.ip_clusters.length > 0 && (
              <div className="px-5 py-3">
                <p className="text-xs text-amber-400 font-medium mb-2">{'>='}5 referred signups from same IP (recent sample)</p>
                <ul className="space-y-1">
                  {fraud_signals.ip_clusters.map((c) => (
                    <li key={`${c.inviter_id}-${c.ip}`} className="text-xs text-slate-300 font-mono flex justify-between">
                      <span>inviter={c.inviter_id.slice(0, 8)} · ip={c.ip}</span>
                      <span className="text-amber-400">{c.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fraud_signals.approaching_daily_cap.length > 0 && (
              <div className="px-5 py-3">
                <p className="text-xs text-amber-400 font-medium mb-2">{'>='}7 bonuses today</p>
                <ul className="space-y-1">
                  {fraud_signals.approaching_daily_cap.map((c) => (
                    <li key={c.inviter_id} className="text-xs text-slate-300 font-mono flex justify-between">
                      <span>inviter={c.inviter_id.slice(0, 8)}</span>
                      <span className={c.bonuses_today >= 10 ? 'text-red-400' : 'text-amber-400'}>
                        {c.bonuses_today}{c.bonuses_today >= 10 ? ' (capped)' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fraud_signals.at_lifetime_cap.length > 0 && (
              <div className="px-5 py-3">
                <p className="text-xs text-red-400 font-medium mb-2">At lifetime cap (100 bonuses)</p>
                <ul className="space-y-1">
                  {fraud_signals.at_lifetime_cap.map((c) => (
                    <li key={c.inviter_id} className="text-xs text-slate-300 font-mono flex justify-between">
                      <span>inviter={c.inviter_id.slice(0, 8)}</span>
                      <span className="text-red-400">{c.lifetime_bonuses}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyState title="No fraud signals right now" body="Self-referrals, IP clusters, and cap blocks will surface here." />
      )}
    </div>
  );
}
